/**
 * the ClientsService class handles clients/customers requests
 * http://stackoverflow.com/questions/34755350/using-http-rest-apis-with-angular-2/34758630#34758630 (see accepted answer by Pardeep Jain)
 * http://stackoverflow.com/questions/37290672/passing-parameters-in-http-get-using-angular-2 (see accepted answer by Pardeep Jain)
 * https://angular.io/docs/ts/latest/guide/server-communication.html#!#error-handling
 * by ole fjarestad
 */

import { Injectable } from '@angular/core';
import { Http, Headers, Request, RequestOptions, RequestMethod, Response } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import { api } from './api';
import { UserAuthService } from './user-auth.service';
import { Client } from './client';


@Injectable()
export class ClientsService {

	private clientsUrl: string = api.base + '/customers';
	private clientContactsUrl: string = api.base + '/customers-contacts';
	private clientAddressesUrl: string = api.base + '/customers-addresses';

	constructor(
		private http: Http,
		private userAuthService: UserAuthService
	) { }

	// private getHeaders( requestOptions: object ) {
	private getRequestOptions( options: any ) {
		let headers = new Headers();
		headers.append( 'Accept', 'application/json' );
		headers.append( 'Authorization', 'Bearer ' + this.userAuthService.getToken() );
		options.headers = headers;
		let requestOptions = new RequestOptions(options);
		return requestOptions;
	}

	private handleError(error: Response | any) {
		return Observable.throw(error);
	}

	getClient(id: number): Observable<{}> {

		let requestOptions = {
			withCredentials: true,
			method: RequestMethod.Get,
			url: this.clientsUrl + '/' + id,
			// params: 'expand=users', // #RPT: need to test if this works, and how to pass multiple params (http://stackoverflow.com/questions/34475523/how-to-pass-url-arguments-query-string-to-a-http-request-on-angular-2)
			params: {
				expand: 'users,contacts,comments,addresses,user_relations,cat',
			},
		}

		// just for debugging
		// console.log(new Request(this.getRequestOptions(requestOptions)));

		let client$ = this.http
						.request( new Request(this.getRequestOptions(requestOptions)) )
						// .do( (res: Response) => console.log( res ) )
						.map( (res: Response) => res.json() )
						// .do( (res: Response) => console.log( res ) )
						.catch( this.handleError );

		return client$;
	}

	getClients(page: number = 1, query: string = '', currentUserID: number = 0): Observable<[{}]> {

		let requestOptions = {
			withCredentials: true,
			method: RequestMethod.Get,
			url: this.clientsUrl,
			// url: this.clientsUrl + '/search',
			// params: 'expand=users', // #RPT: need to test if this works, and how to pass multiple params (http://stackoverflow.com/questions/34475523/how-to-pass-url-arguments-query-string-to-a-http-request-on-angular-2)
			params: {
				expand: 'users,contacts,comments,addresses,cat', // maybe we should make these as params for the method?
				user: currentUserID,
				page: page,
				// name: '', // search in client name
				query: query,
			},
		}

		let clients$ = this.http
						.request( new Request(this.getRequestOptions(requestOptions)) )
						// .do( (res: Response) => console.log( res ) )
						.map( (res: Response) => res.json() )
						// .do( (res: Response) => console.log( res ) )
						.catch( this.handleError );

		return clients$;
	}

	createClient(info: {}): Observable<{}> {

		let requestOptions = {
			withCredentials: true,
			method: RequestMethod.Post,
			url: this.clientsUrl,
			body: info,
		}

		let client$ = this.http
						.request( new Request(this.getRequestOptions(requestOptions)) )
						// .do( (res: Response) => console.log( res ) )
						.map( (res: Response) => res.json() )
						// .do( (res: Response) => console.log( res ) )
						.catch( this.handleError );

		return client$;
	}

	updateClient(id: number, info: {}): Observable<{}> {

		let requestOptions = {
			withCredentials: true,
			method: RequestMethod.Put,
			// url: this.clientsUrl,
			url: this.clientsUrl + '/' + id,
			// search: id, // will this work? https://angular.io/docs/ts/latest/api/http/index/RequestOptions-class.html
			body: info,
			// params: {
			// 	expand: 'users',
			// },
		}

		let client$ = this.http
						.request( new Request(this.getRequestOptions(requestOptions)) )
						// .do( (res: Response) => console.log( res ) )
						.map( (res: Response) => res.json() )
						// .do( (res: Response) => console.log( res ) )
						.catch( this.handleError );

		return client$;
	}

	deleteClient(id: number): Observable<{}> {

		let requestOptions = {
			withCredentials: true,
			method: RequestMethod.Delete,
			url: this.clientsUrl + '/' + id,
		}

		let client$ = this.http
						.request( new Request(this.getRequestOptions(requestOptions)) )
						// .do( (res: Response) => console.log( res ) )
						.map( (res: Response) => res.json() )
						// .do( (res: Response) => console.log( res ) )
						.catch( this.handleError );

		return client$;
	}

	searchClients(searchQuery: string): Observable<Client[]> {

		// return new Observable<Client[]>();

		let requestOptions = {
			withCredentials: true,
			method: RequestMethod.Get,
			url: this.clientsUrl, // #RPT: need an endpoint!
			params: {
				expand: 'users,comments', // maybe we should make these as params for the method?
			},
		}

		let result$ = this.http
						.request( new Request(this.getRequestOptions(requestOptions)) )
						// .do( (res: Response) => console.log( res ) )
						.map( (res: Response) => res.json() as Client[] )
						// .do( (res: Response) => console.log( res ) )
						.catch( this.handleError );

		return result$;

	}

	createClientContact( customerID: number, name: string, title?: string, email?: string, phone?: string, phoneExtra?: string, comment?: string ) {

		let requestOptions = {
			withCredentials: true,
			method: RequestMethod.Post,
			url: this.clientContactsUrl, // #RPT: need an endpoint!
			body: {
				customer_id: customerID,
				name: name,
				title: title,
				email: email,
				phone: phone,
				phone_extra: phoneExtra,
				comment: comment,
			}
		}

		let contact$ = this.http
						.request( new Request(this.getRequestOptions(requestOptions)) )
						// .do( (res: Response) => console.log( res ) )
						.map( (res: Response) => res.json() )
						// .do( (res: Response) => console.log( res ) )
						.catch( this.handleError );

		return contact$;

	}

	updateClientContact( contactID: number, name?: string, title?: string, email?: string, phone?: string, phoneExtra?: string, comment?: string ) {

		let requestOptions = {
			withCredentials: true,
			method: RequestMethod.Put,
			url: this.clientContactsUrl + '/' + contactID,
			body: {
				name: name,
				title: title,
				email: email,
				phone: phone,
				phone_extra: phoneExtra,
				comment: comment,
			}
		}

		let contact$ = this.http
						.request( new Request(this.getRequestOptions(requestOptions)) )
						// .do( (res: Response) => console.log( res ) )
						.map( (res: Response) => res.json() )
						// .do( (res: Response) => console.log( res ) )
						.catch( this.handleError );

		return contact$;

	}

	deleteClientContact( contactID: number, endedBy: number, endedAt: string ) {

		let requestOptions = {
			withCredentials: true,
			method: RequestMethod.Put,
			url: this.clientContactsUrl + '/' + contactID,
			body: {
				ended_by: endedBy,
				ended_at: endedAt,
			}
		}

		let contact$ = this.http
						.request( new Request(this.getRequestOptions(requestOptions)) )
						// .do( (res: Response) => console.log( res ) )
						.map( (res: Response) => res.json() )
						// .do( (res: Response) => console.log( res ) )
						.catch( this.handleError );

		return contact$;

	}






	createClientAddress( customerID: number, name: string, street: string, postalCode: string, postalCity: string ) {

		let requestOptions = {
			withCredentials: true,
			method: RequestMethod.Post,
			url: this.clientAddressesUrl,
			body: {
				customer_id: customerID,
				name: name,
				street: street,
				postal_code: postalCode,
				postal_city: postalCity,
			}
		}

		let contact$ = this.http
						.request( new Request(this.getRequestOptions(requestOptions)) )
						// .do( (res: Response) => console.log( res ) )
						.map( (res: Response) => res.json() )
						// .do( (res: Response) => console.log( res ) )
						.catch( this.handleError );

		return contact$;

	}

	updateClientAddress( addressID: number, name?: string, street?: string, postalCode?: string, postalCity?: string ) {

		let requestOptions = {
			withCredentials: true,
			method: RequestMethod.Put,
			url: this.clientAddressesUrl + '/' + addressID,
			body: {
				name: name,
				street: street,
				postal_code: postalCode,
				postal_city: postalCity,
			}
		}

		let address$ = this.http
						.request( new Request(this.getRequestOptions(requestOptions)) )
						// .do( (res: Response) => console.log( res ) )
						.map( (res: Response) => res.json() )
						// .do( (res: Response) => console.log( res ) )
						.catch( this.handleError );

		return address$;

	}

	deleteClientAddress( addressID: number, endedBy: number, endedAt: string ) {

		let requestOptions = {
			withCredentials: true,
			method: RequestMethod.Put,
			url: this.clientAddressesUrl + '/' + addressID,
			body: {
				ended_by: endedBy,
				ended_at: endedAt,
			}
		}

		let address$ = this.http
						.request( new Request(this.getRequestOptions(requestOptions)) )
						// .do( (res: Response) => console.log( res ) )
						.map( (res: Response) => res.json() )
						// .do( (res: Response) => console.log( res ) )
						.catch( this.handleError );

		return address$;

	}



}



// WEBPACK FOOTER //
// ./src/app/clients.service.ts